export interface PinSignInPath {
  orgCode: string;
  siteCode: string | null;
  memberId: string | null;
}

const CODE = "[a-z0-9][a-z0-9_-]*";
const MEMBER = "[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}";
const PIN_SIGN_IN_PATH = new RegExp(`^/o/(${CODE})(?:/(${CODE})(?:/(${MEMBER}))?)?/?$`, "i");

/**
 * Matches the bookmarked PIN sign-in link. `/o/<org-code>` lists everyone
 * with a PIN; `/o/<org-code>/<site-code>` lists only the people who work at
 * that site (plus organisation-wide roles); `/o/<org-code>/<site-code>/<member-id>`
 * is one person's own link and opens straight to their PIN pad.
 */
export function matchPinSignInPath(pathname: string): PinSignInPath | null {
  const match = PIN_SIGN_IN_PATH.exec(pathname);
  return match
    ? { orgCode: match[1], siteCode: match[2] ?? null, memberId: match[3]?.toLowerCase() ?? null }
    : null;
}

export function pinSignInLink(
  origin: string, orgCode: string, siteCode?: string | null, memberId?: string | null,
): string {
  const site = siteCode ? `/${siteCode}` : "";
  const member = siteCode && memberId ? `/${memberId}` : "";
  return `${origin}/o/${orgCode}${site}${member}`;
}

type LinkStorage = Pick<Storage, "getItem" | "setItem" | "removeItem" | "key" | "length">;

const REMEMBERED_PIN_LINK_KEY = "kitchen-hub-pin-link";

/**
 * The PIN link this device last signed in from. Unlike the PIN session it
 * survives sign-out and expiry, so a chef who opens the app at `/` (the
 * home-screen icon, a typed address) gets their PIN pad again rather than the
 * email and password screen they have no account for.
 */
export function readRememberedPinLink(storage: LinkStorage | null): PinSignInPath | null {
  try {
    const path = storage?.getItem(REMEMBERED_PIN_LINK_KEY);
    return path ? matchPinSignInPath(path) : null;
  } catch {
    return null;
  }
}

export function rememberPinLink(
  storage: LinkStorage | null, orgCode: string | null, siteCode?: string | null, memberId?: string | null,
): void {
  try {
    if (orgCode) storage?.setItem(REMEMBERED_PIN_LINK_KEY, pinSignInLink("", orgCode, siteCode, memberId));
    else storage?.removeItem(REMEMBERED_PIN_LINK_KEY);
  } catch {
    // Remembering the link is a convenience; the full link still works.
  }
}

/** Whether an email-and-password (Supabase) sign-in is stored on this device,
 * in which case `/` belongs to that account and must not jump to a PIN pad. */
export function hasStoredPasswordSession(storage: LinkStorage | null): boolean {
  try {
    if (!storage) return false;
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) return true;
    }
    return false;
  } catch {
    return false;
  }
}
